import { DropdownPrimeReact } from "@finanedi.es/storybook"
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query"
import { EntidadBancariaDtoProps } from "api/DTOs/EntidadBancariaDto"
import { getEntidadesBancarias } from "api/EntidadBancaria.services"
import { EntidadBancariaDtoIPagedEnumerableProps } from "api/Schemas/EntidadBancariaDtoIPagedEnumerable"
import { VirtualScrollerLazyParams } from "primereact/virtualscroller"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

export interface DropdownEntidadesBancariasProps {
  value?: string | null | undefined
  onChange: (value: EntidadBancariaDtoProps) => void
  prefilter?: Partial<EntidadBancariaDtoProps>
  disabled?: boolean
  className?: string
}

type DropdownOptionsEntidadesBancariasProps =
  | Pick<EntidadBancariaDtoProps, "id" | "nombre" | "bic">
  | undefined
  | null

export const DropdownEntidadesBancarias = ({
  value = undefined,
  onChange,
  prefilter,
  disabled = false,
  className = "",
}: DropdownEntidadesBancariasProps) => {
  const { t } = useTranslation()
  const [keyWord, setKeyWord] = useState<string | undefined>(undefined)

  const [entidadesBancarias, setSetEntidadesBancarias] = useState<
    DropdownOptionsEntidadesBancariasProps[]
  >([])
  const {
    data: dataBank,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["bankListNames", keyWord],
    queryFn: ({ pageParam = 0 }) => {
      return getEntidadesBancarias({
        SkipCount: pageParam,
        Nombre: keyWord,
      })
    },
    getNextPageParam: (lastPage: EntidadBancariaDtoIPagedEnumerableProps) => {
      return lastPage?.currentPage &&
        lastPage?.totalCount &&
        lastPage.currentPage * 10 < lastPage.totalCount
        ? lastPage.currentPage * 10
        : undefined
    },
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false,
  })

  useEffect(() => {
    const nuevosBancos: DropdownOptionsEntidadesBancariasProps[] =
      dataBank?.pages
        ?.flatMap((page) => page?.items)
        ?.filter((entidad) => entidad !== null && entidad !== undefined) ?? []

    if (nuevosBancos && nuevosBancos.length > 0) {
      setSetEntidadesBancarias([
        ...nuevosBancos.map((banco) => ({
          id: banco?.id,
          nombre: banco?.nombre,
          bic: banco?.bic,
        })),
        ...(prefilter?.id &&
        !nuevosBancos.some((banco) => banco?.id === prefilter.id)
          ? [prefilter]
          : []),
      ])
    }
  }, [dataBank, prefilter])

  const loadMoreData = (event: VirtualScrollerLazyParams) => {
    if (!isFetchingNextPage && event.last === entidadesBancarias?.length) {
      if (
        dataBank &&
        dataBank.pages &&
        dataBank.pages.length > 0 &&
        entidadesBancarias.length <= (dataBank.pages[0]?.totalCount ?? 0)
      ) {
        fetchNextPage()
      }
    }
  }

  const onContactoFilter = async ({ filter }: { filter: string }) => {
    setKeyWord(filter)
  }

  const onChangeValueComplete = (value: string) => {
    const entidadSelected = entidadesBancarias.find(
      (entidad) => entidad?.id === value
    )
    onChange(entidadSelected ?? {})
  }

  return (
    <DropdownPrimeReact
      value={value}
      placeholder={`${t("remesas.edit.form.placeholderContacto")}`}
      onChange={(e) => {
        onChangeValueComplete(e.value)
      }}
      style={{ maxWidth: "100%" }}
      options={entidadesBancarias ?? []}
      optionLabel='nombre'
      optionValue='id'
      panelClassName='!tw-w-96 !tw-text-sm'
      className={`!tw-rounded [&>:nth-child(3)]:!tw-text-sm !tw-max-w-full !tw-w-full tw-border-1 !tw-h-10 tw-items-center tw-align-middle !tw-p-0 ${className}`}
      filter
      emptyFilterMessage={`${t(
        "contactos.cuentasBancarias.notElementAccountsFilter"
      )}`}
      emptyMessage={`${t("contactos.cuentasBancarias.notElementAccounts")}`}
      scrollHeight='350px'
      filterMatchMode='contains'
      onFilter={onContactoFilter}
      virtualScrollerOptions={{
        lazy: true,
        onLazyLoad: loadMoreData,
        itemSize: 40,
        showLoader: false,
      }}
      disabled={disabled}
    />
  )
}
